import * as React from 'react';

import { Message } from '@independent-software/typeui/controls/Message';
import { Icon } from '@independent-software/typeui/controls/Icon';
import { Button } from '@independent-software/typeui/controls/Button';
import { Tabs } from '@independent-software/typeui/controls/Tabs';

import { App } from '../../App';
import { Container, Content, BottomBar, Section, Upload } from '../../modules';
import { System, SystemFactory } from '../../resource';
import { Query } from '../../services';
import { DashboardCard } from './DashboardCard';

import { ProjectMap } from './ProjectMap';
import { AuthContext } from '../../AuthContext';
import { FilterContext } from '../../FilterContext';
import { Gate } from '../../controls/Gate';
import { Loader } from '@independent-software/typeui/controls/Loader';

type TImportType = 'none' | 'barcawin' | 'flat';

const Dashboard = () => {
  const auth = React.useContext(AuthContext);
  const filter = React.useContext(FilterContext);

  const [systems, setSystems] = React.useState([] as System[]);
  const [loading, setLoading] = React.useState(true);
  const [importing, setImporting] = React.useState('none' as TImportType);

  const loadSystems = () => {
    let query = new Query('name', 'asc');
    SystemFactory.getSome(auth, 0, 999, query)
      .then((res) => { 
        setSystems(res.items);
        // Is this the initial load of systems? Then set
        // the active systems list.
        if(filter.mustLoadSystems) {
          filter.setMustLoadSystems(false);
          filter.setActiveSystems(res.items.map(s => s.id));
        }
        setLoading(false);
      });    
  }

  React.useEffect(() => loadSystems(), []);

  const handleBarcawinImport = () => {
    setImporting('barcawin');
  }

  const handleFlatImport = () => {
    setImporting('flat');
  }

  const handleCloseUpload = () => {
    setImporting('none');
  }

  const handleUploadSuccess = () => {
    loadSystems();
  }

  const toggleSystem = (system: System) => {
    const idx = filter.activeSystems.indexOf(system.id);
    if(idx != -1) {
      filter.activeSystems.splice(idx, 1);
    } else {
      filter.activeSystems.push(system.id);
    }
    filter.setActiveSystems([...filter.activeSystems]);
  }

  // Deselect all systems.
  const handleDeselectAll = () => {
    filter.setActiveSystems([]);  
  }

  // Select all systems.
  const handleSelectAll = () => {
    filter.setActiveSystems(systems.map(s => s.id));
  }

  return (
    <Container>
      {loading == true && <Loader/>}
      <Content>
        <Tabs underlined nohiddenrender>
          <Tabs.Pane label="Systems">
            {loading == false && 
              <Section padded="very">
                {systems.map((s, index) => 
                  <DashboardCard key={index} system={s} active={filter.activeSystems.includes(s.id)} onClick={toggleSystem}/>)}
                {systems.length == 0 && 
                  <Message type="info" icon>
                    <Icon name="bullhorn" size="big"/>
                    <Message.Content>
                      <Message.Header>No systems</Message.Header>
                      There are currently no systems in the database.
                    </Message.Content>
                  </Message>
                }
              </Section>}
          </Tabs.Pane>
          <Tabs.Pane label="Map">
            <ProjectMap systemIDs={systems.filter(s => filter.activeSystems.includes(s.id)).map(s => s.id)}/>
          </Tabs.Pane>
        </Tabs>
      </Content>
      <BottomBar>
        <div>
          <Button onClick={handleSelectAll}><Icon name="plus"/> Select all</Button>
          <Button onClick={handleDeselectAll}><Icon name="minus" />Deselect all</Button>
        </div>
        <div>
          <Gate right='can_view_logs'>
            <>
              <Button secondary onClick={handleBarcawinImport}>Import BarcaWin</Button>
              <Button secondary onClick={handleFlatImport}>Import flat file</Button>
            </>
          </Gate>
        </div>
      </BottomBar>
      <Upload
        auth={auth}
        open={importing != 'none'}
        url={App.config.apiURL + "systems/" + (importing == 'barcawin' ? 'import-barcawin' : 'import-flat') }
        title="Import systems"
        onClose={handleCloseUpload}
        onSuccess={handleUploadSuccess}
      />
    </Container>
  );
}

export { Dashboard }
